import React, { useEffect, useRef, useMemo } from "react";
import { format } from "date-fns";
import { formatMessageTimestamp } from "utils/formatMessageTimestamp";
import CheckingNotificationsSVG from "../../assets/checking-notifications.svg";
import { Box, Stack, Avatar, Typography, Divider } from "@mui/material";
import { useHistory } from "react-router-dom";
import FilePreview from "./FilePreview";
import getAvatarInitials from "utils/getAvatarInitials";

const ThreadList = ({ messages = [], teamMemberName, userId, onIconPress, redirectMsgId }) => {
  const history = useHistory();
  const scrollRef = useRef(null);
  const prevMessagesLength = useRef(0);
  const messageRefs = useRef({});
  const lastRedirectedMsgId = useRef(null);

  useEffect(() => {
    const firstLoad = prevMessagesLength.current === 0 && messages.length > 0;
    const newMessageAdded = messages.length > prevMessagesLength.current;

    const shouldScrollToRedirect =
      redirectMsgId &&
      messageRefs.current[redirectMsgId] &&
      redirectMsgId !== lastRedirectedMsgId.current;

    if (shouldScrollToRedirect) {
      lastRedirectedMsgId.current = redirectMsgId;
      messageRefs.current[redirectMsgId].scrollIntoView({
        behavior: "auto",
        block: "end",
      });
      history.replace("/messages");
    } else if ((firstLoad || newMessageAdded) && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }

    prevMessagesLength.current = messages.length;
  }, [messages, redirectMsgId]);

  // groups the messages by date sent
  const groupMessagesByDate = (messages) => {
    if (!messages || messages.length === 0) return {};
    return messages.reduce((acc, msg) => {
      const dateKey = format(new Date(msg.created_date), "yyyy-MM-dd");
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(msg);
      return acc;
    }, {});
  };

  const groupedMessages = useMemo(() => groupMessagesByDate(messages), [messages]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        p: "0px 24px 24px",
      }}
    >
      {/* Fade-out gradient overlay at top */}
      <Box
        sx={{
          position: "absolute",
          inset: "0 0 auto 0",
          height: "48px",
          zIndex: 1,
          background: "linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, #FFF 72%)",
        }}
      />

      {messages?.length === 0 ? (
        <Box
          sx={{
            inset: "auto auto 0 0",
            ml: "24px",
            position: "absolute",
          }}
        >
          <Box component="img" src={CheckingNotificationsSVG} alt="" />
          <Typography variant="h3" fontFamily={"TestTiemposText"}>
            Say hello!
          </Typography>
          <Typography variant="body1" color="text.primary">
            This is the beginning of your chat with{" "}
            <Typography component="span" variant="subtitle1">
              {teamMemberName}
            </Typography>
          </Typography>
        </Box>
      ) : (
        <Stack
          ref={scrollRef}
          sx={{
            gap: "24px",
            overflowY: "auto",
            height: "100%",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          {Object.entries(groupedMessages)
            .sort(([dateA], [dateB]) => new Date(dateA) - new Date(dateB))
            .map(([date, msgs]) => (
              <Stack key={date} mb={2}>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={2}
                  sx={{ padding: "23px 0px", mb: "24px" }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {formatMessageTimestamp(date, true)}
                  </Typography>
                  <Divider sx={{ flex: 1 }} />
                </Stack>

                <Stack sx={{ gap: "24px" }}>
                  {msgs.map((msg) => (
                    <Stack
                      ref={(el) => {
                        if (el) messageRefs.current[msg.pkey] = el;
                      }}
                      key={msg.pkey}
                      alignItems="flex-start"
                    >
                      <Stack direction="row" gap="12px" alignItems="center">
                        <Avatar src={msg?.sender?.image_url} sx={{ width: 32, height: 32 }}>
                          {getAvatarInitials(`${msg.sender?.first_name} ${msg.sender?.last_name}`)}
                        </Avatar>
                        <Typography variant="subtitle1">
                          {msg.sender_user_id === userId
                            ? "You"
                            : `${msg.sender?.first_name} ${msg.sender?.last_name}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format(new Date(msg.created_date), "p")}
                        </Typography>
                      </Stack>
                      {msg.message && (
                        <Typography variant="body2" color="text.secondary" sx={{ ml: "44px" }}>
                          {msg.message}
                        </Typography>
                      )}
                      {msg.metadata && msg.metadata.fileName && (
                        <Stack mt="12px" sx={{ pl: "44px", width: "100%" }}>
                          <FilePreview
                            onIconPress={onIconPress}
                            message={msg}
                            hasUploadedToClientAccount={msg.metadata.uploadedToClientAccount}
                          />
                        </Stack>
                      )}
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            ))}
        </Stack>
      )}
    </Box>
  );
};

export default ThreadList;
